/**
 * PubSub VCR - Pub/Sub Timeline Recorder and Player
 *
 * Wraps PubSub.publish() (and publishSync()) so every broadcast can be recorded
 * to an in-memory tape, then replayed - into the same session, or into a
 * developer's session from a customer's pasted JSON.
 *
 * The wrapper is installed unconditionally (dev and prod) but records nothing
 * until startVCR() is called - the cost while idle is a boolean check per publish.
 *
 * Usage (app entry point, immediately after importing PubSub):
 *   import PubSub from 'pubsub-js';
 *   import { installPubSubVCR } from '../../../shared/pubsubVCR.js';
 *   installPubSubVCR(PubSub);
 *
 * Console - record:
 *   startVCR()      // clear the tape and begin recording
 *   stopVCR()       // stop, save vcr-<app>-<timestamp>.json, return the JSON string
 *   stopVCR({ save: false })   // stop without writing a file
 *   saveVCR()       // write the current tape to a file without stopping
 *   _vcrTimeline    // live tape (array), readable while recording
 *
 * Console - play back:
 *   await loadVCR() // pick a saved .json from disk
 *   loadVCR(json)   // or load from a pasted stopVCR() string / array
 *   playVCR()       // replay the tape at original speed
 *   playVCR({ dryRun: true })            // list what would fire, publish nothing
 *   playVCR({ speed: 4, maxGap: 500 })   // 4x, and never wait longer than 500ms
 *   playVCR({ skip: ['AUTH', 'LLM'] })   // skip topics under these prefixes
 *   stopPlayback()  // cancel a replay in progress
 *
 * Replay publishes through the *unwrapped* publish, so a replay never records
 * itself, and recording is force-stopped when playback starts.
 *
 * WARNING: replayed events hit real subscribers. A tape containing AUTH.SIGNOUT
 * will really sign you out; LLM.PROMPT will really call the model. Use dryRun
 * or skip when in doubt.
 *
 * NOTE: PubSub is passed in rather than imported - `shared/` lives outside the
 * pnpm workspaces, so bare specifiers like 'pubsub-js' do not resolve from here.
 */

const DEFAULT_MAX_ENTRIES = 5000;

// Idle gaps longer than this are clamped during replay - nobody wants to sit
// through the four minutes a customer spent reading the screen.
const DEFAULT_MAX_GAP_MS = 2000;

// Topics whose replay has real side effects worth warning about up front.
const HIGH_IMPACT = /^(AUTH|ROUTE|LLM)\./;

/**
 * pubsub-js topics are hierarchical, so a prefix match must respect the dot
 * boundary: 'AUTH' matches 'AUTH.SIGNIN.GO' but not 'AUTHORIZE.X'.
 */
function matchesPrefix(topic, prefixes) {
  return prefixes.some((p) => topic === p || topic.startsWith(`${p}.`));
}

/**
 * Filename-safe local timestamp: 2026-07-31T18-42-05
 * Local rather than UTC - it is read by a human comparing it against when the
 * customer said the problem happened. The entries keep epoch times regardless.
 */
function fileTimestamp(now = new Date()) {
  const p = (n) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}` +
    `T${p(now.getHours())}-${p(now.getMinutes())}-${p(now.getSeconds())}`;
}

/**
 * Save text to the user's filesystem via an object-URL anchor.
 *
 * Deliberately not showSaveFilePicker(): that is Chromium-only, and a customer
 * on Firefox or Safari is exactly who we ask to produce a tape. The anchor path
 * works everywhere and lands in Downloads without a permission prompt.
 */
function downloadJSON(json, filename) {
  const url = URL.createObjectURL(new Blob([json], { type: 'application/json' }));
  const a = document.createElement('a');

  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  a.remove();

  // Revoking synchronously can cancel the download in some browsers.
  setTimeout(() => URL.revokeObjectURL(url), 10000);

  return filename;
}

/**
 * Prompt for a local .json file and resolve its text.
 *
 * Note: the input's 'cancel' event is only recent-ish; where it is missing,
 * dismissing the dialog simply leaves the promise pending rather than rejecting.
 */
function pickJSONFile() {
  return new Promise((resolve, reject) => {
    const input = document.createElement('input');

    input.type = 'file';
    input.accept = 'application/json,.json';
    input.style.display = 'none';

    input.addEventListener('change', () => {
      const file = input.files && input.files[0];
      input.remove();
      if (!file) {
        reject(new Error('[VCR] no file selected'));
        return;
      }
      file.text().then(resolve, reject);
    });

    input.addEventListener('cancel', () => {
      input.remove();
      reject(new Error('[VCR] file selection cancelled'));
    });

    document.body.appendChild(input);
    input.click();
  });
}

/**
 * Position of an entry on the tape. `elapsed` is the recorder's own clock;
 * `timestamp` is the fallback for hand-edited or older tapes.
 */
function timeOf(entry) {
  return typeof entry.elapsed === 'number' ? entry.elapsed : (entry.timestamp ?? 0);
}

/**
 * Cancellable sleep. Cancelling resolves the promise rather than leaving the
 * replay loop parked on a timer that will never fire.
 */
function sleep(ms, token) {
  if (ms <= 0) return Promise.resolve();

  return new Promise((resolve) => {
    token.wake = resolve;
    token.timer = setTimeout(() => {
      token.timer = null;
      resolve();
    }, ms);
  });
}

/**
 * Deep-copy publish payloads so later mutation of the object does not rewrite
 * history. structuredClone rejects functions, DOM nodes, Errors' prototypes,
 * proxies and class instances carrying methods - all of which show up in
 * web-component event.detail payloads - so degrade to a shallow description
 * rather than letting a recorder failure break the publish.
 */
function snapshotData(data) {
  if (data === null || typeof data !== 'object') return data;

  try {
    return structuredClone(data);
  } catch {
    return describeShallow(data);
  }
}

/**
 * Last-resort snapshot: keep primitives, replace anything structured with its
 * type tag. Never throws.
 */
function describeShallow(data) {
  const out = { _vcrPartial: true };

  for (const key of Object.keys(data)) {
    try {
      const value = data[key];
      out[key] = (value === null || typeof value !== 'object')
        ? value
        : `[${Object.prototype.toString.call(value).slice(8, -1)}]`;
    } catch (err) {
      out[key] = `[unreadable: ${err.message}]`;
    }
  }

  return out;
}

/**
 * Serialize the tape. structuredClone preserves cycles, so a plain
 * JSON.stringify can throw - swap repeat references for a marker instead.
 */
function serializeTape(tape) {
  const seen = new WeakSet();

  return JSON.stringify(tape, (key, value) => {
    if (typeof value === 'bigint') return `${value}n`;
    if (typeof value === 'function') return '[Function]';
    if (typeof value === 'object' && value !== null) {
      if (seen.has(value)) return '[Circular]';
      seen.add(value);
    }
    return value;
  }, 2);
}

/**
 * Install the recorder onto a pubsub-js instance.
 *
 * @param {object} PubSub - the pubsub-js singleton
 * @param {object} [options]
 * @param {number} [options.maxEntries=5000] - tape cap; oldest entries are dropped
 * @param {string} [options.app=''] - label stamped on each entry (which client produced the tape)
 * @returns {object|null} the VCR control object, or null if PubSub looks wrong
 */
export function installPubSubVCR(PubSub, options = {}) {
  if (!PubSub || typeof PubSub.publish !== 'function') {
    console.warn('[VCR] PubSub.publish not found - recorder not installed');
    return null;
  }

  // Idempotent: Vite HMR re-runs entry modules, and double-wrapping would
  // record every publish twice.
  if (PubSub.publish.__vcr) return PubSub.publish.__vcr;

  const maxEntries = options.maxEntries ?? DEFAULT_MAX_ENTRIES;
  const app = options.app ?? '';

  const tape = [];
  const originals = {};
  let seq = 0;
  let startedAt = 0;
  let dropped = 0;
  let playToken = null;

  // Exposed with the names remote support already types. The window flag is the
  // source of truth for the hot path, so it can also be flipped by hand.
  window._vcrTimeline = tape;
  window._isVCRRecording = false;

  function record(message, data, isSync) {
    if (tape.length >= maxEntries) {
      tape.shift();
      dropped++;
    }

    const now = Date.now();
    const entry = {
      seq: seq++,
      message,
      data: snapshotData(data),
      timestamp: now,
      elapsed: now - startedAt
    };

    if (isSync) entry.sync = true;
    if (app) entry.app = app;

    tape.push(entry);
  }

  const vcr = {
    /** Clear the tape and begin recording. */
    start() {
      tape.length = 0;
      seq = 0;
      dropped = 0;
      startedAt = Date.now();
      window._isVCRRecording = true;
      console.info(`[VCR] recording (cap ${maxEntries} events) - reproduce the issue, then run stopVCR()`);
      return true;
    },

    /**
     * Stop recording, save the tape to a timestamped file, and return it as a
     * JSON string. Pass { save: false } to skip the download.
     */
    stop(options = {}) {
      const { save = true, filename } = options;

      window._isVCRRecording = false;

      if (dropped) {
        console.warn(`[VCR] tape capped at ${maxEntries} - ${dropped} earlier event(s) dropped`);
      }
      console.info(`[VCR] stopped - ${tape.length} event(s) recorded`);

      const json = serializeTape(tape);
      if (save && tape.length) vcr.save(filename, json);

      return json;
    },

    /**
     * Write the current tape to a timestamped JSON file without stopping.
     *
     * @param {string} [filename] - defaults to vcr-<app>-<local timestamp>.json
     * @returns {string|null} the filename written, or null if saving failed
     */
    save(filename, json) {
      const name = filename || `vcr-${app || 'pubsub'}-${fileTimestamp()}.json`;

      try {
        downloadJSON(json ?? serializeTape(tape), name);
        console.info(`[VCR] saved ${tape.length} event(s) to ${name}`);
        return name;
      } catch (err) {
        // Never let a failed download lose the tape - the caller still has the
        // JSON, and _vcrTimeline is still in memory.
        console.error('[VCR] could not save file - copy _vcrTimeline instead', err);
        return null;
      }
    },

    /** Read the tape without stopping the recording. */
    peek() {
      return tape;
    },

    /**
     * Load a tape produced by stop(). Replaces the current tape, so
     * _vcrTimeline stays the one live reference.
     *
     * @param {string|Array} [source] - JSON string or array; omit to open a
     *   file picker for a saved .json (returns a promise in that case)
     * @returns {number|Promise<number>} entries loaded
     */
    load(source) {
      if (source === undefined) {
        return pickJSONFile().then((text) => vcr.load(text));
      }

      const parsed = typeof source === 'string' ? JSON.parse(source) : source;

      if (!Array.isArray(parsed)) {
        throw new TypeError('[VCR] load() expects the JSON string from stopVCR(), or an array');
      }

      window._isVCRRecording = false;
      tape.length = 0;
      tape.push(...parsed);
      seq = tape.length;
      dropped = 0;

      const from = parsed[0]?.app;
      console.info(`[VCR] loaded ${tape.length} event(s)${from ? ` from ${from}` : ''} - playVCR() to replay`);
      return tape.length;
    },

    /**
     * Replay the tape into the live app.
     *
     * @param {object} [options]
     * @param {number} [options.speed=1] - 1 = original timing, 0 = no waiting at all
     * @param {number} [options.maxGap=2000] - clamp for idle gaps, in ms
     * @param {boolean} [options.dryRun=false] - log the sequence, publish nothing
     * @param {string[]} [options.skip=[]] - topic prefixes to leave out
     * @param {boolean} [options.quiet=false] - suppress the per-event log
     * @returns {Promise<object>} summary: { played, skipped, cancelled, durationMs }
     */
    async play(options = {}) {
      const {
        speed = 1,
        maxGap = DEFAULT_MAX_GAP_MS,
        dryRun = false,
        skip = [],
        quiet = false
      } = options;

      if (playToken) {
        console.warn('[VCR] a replay is already running - stopPlayback() first');
        return { played: 0, skipped: 0, cancelled: true, durationMs: 0 };
      }
      if (!tape.length) {
        console.warn('[VCR] tape is empty - record with startVCR(), or loadVCR(json)');
        return { played: 0, skipped: 0, cancelled: false, durationMs: 0 };
      }

      // Replaying while recording would overwrite the tape mid-read.
      if (window._isVCRRecording) {
        window._isVCRRecording = false;
        console.warn('[VCR] recording stopped - cannot record and replay at once');
      }

      // Snapshot the entries so a concurrent load()/start() cannot mutate the
      // array underneath the loop.
      const events = tape.slice();
      const token = { cancelled: false, timer: null, wake: null };
      playToken = token;

      const partial = events.filter((e) => e.data && e.data._vcrPartial).length;
      if (partial) {
        console.warn(`[VCR] ${partial} event(s) carry degraded payloads (were not cloneable when recorded)`);
      }

      const risky = events.filter((e) => HIGH_IMPACT.test(e.message)).length;
      if (risky && !dryRun) {
        console.warn(
          `[VCR] tape contains ${risky} AUTH/ROUTE/LLM event(s) - replay will really navigate, ` +
          `sign out, or call the model. Try { dryRun: true } or { skip: ['AUTH', 'LLM'] }.`
        );
      }

      console.info(`[VCR] ${dryRun ? 'dry run' : 'replaying'} ${events.length} event(s) at ${speed}x`);

      const beganAt = Date.now();
      let played = 0;
      let skipped = 0;
      let prev = null;

      for (let i = 0; i < events.length; i++) {
        const entry = events[i];

        // Reproduce the original pacing between events.
        if (prev) {
          const recorded = timeOf(entry) - timeOf(prev);
          const gap = Math.min(Math.max(recorded, 0), maxGap);
          await sleep(speed > 0 ? gap / speed : 0, token);
          if (token.cancelled) break;
        }
        prev = entry;

        if (skip.length && matchesPrefix(entry.message, skip)) {
          skipped++;
          if (!quiet) console.debug(`[VCR] ${i + 1}/${events.length} skip ${entry.message}`);
          continue;
        }

        if (!quiet) {
          console.info(`[VCR] ${dryRun ? '·' : '▶'} ${i + 1}/${events.length} ${entry.message}`, entry.data ?? '');
        }

        if (!dryRun) {
          // Publish through the unwrapped method: a replay must never record
          // itself, whatever the recording flag happens to be.
          const publisher = (entry.sync && originals.publishSync) || originals.publish;
          try {
            publisher.call(PubSub, entry.message, entry.data);
          } catch (err) {
            // Only reached when PubSub.immediateExceptions is on. By default
            // pubsub-js re-throws subscriber errors on a later tick, so they
            // surface as uncaught console errors instead - exactly as they
            // would during a normal publish. Delivery continues either way.
            console.error(`[VCR] subscriber threw while replaying ${entry.message}`, err);
          }
        }

        played++;
      }

      const cancelled = token.cancelled;
      playToken = null;

      const summary = { played, skipped, cancelled, durationMs: Date.now() - beganAt };
      console.info(`[VCR] ${cancelled ? 'cancelled' : 'replay complete'}`, summary);
      return summary;
    },

    /** Cancel a replay in progress. */
    stopPlayback() {
      if (!playToken) {
        console.info('[VCR] no replay running');
        return false;
      }

      playToken.cancelled = true;
      if (playToken.timer) clearTimeout(playToken.timer);
      if (playToken.wake) playToken.wake();
      return true;
    },

    /** Restore the untouched publish methods (mainly for tests). */
    uninstall() {
      window._isVCRRecording = false;
      for (const name of ['publish', 'publishSync']) {
        if (PubSub[name] && PubSub[name].__vcrOriginal) {
          PubSub[name] = PubSub[name].__vcrOriginal;
        }
      }
    }
  };

  // publishSync is wrapped too: nothing in the repo calls it today, but a gap
  // there would be an invisible hole in the tape.
  for (const name of ['publish', 'publishSync']) {
    const original = PubSub[name];
    if (typeof original !== 'function') continue;

    const isSync = name === 'publishSync';
    originals[name] = original;

    const wrapped = function (message, data) {
      if (window._isVCRRecording) {
        try {
          record(message, data, isSync);
        } catch (err) {
          // Recording must never break delivery.
          console.warn('[VCR] failed to record', message, err);
        }
      }
      return original.apply(this, arguments);
    };

    wrapped.__vcr = vcr;
    wrapped.__vcrOriginal = original;
    PubSub[name] = wrapped;
  }

  window.startVCR = () => vcr.start();
  window.stopVCR = (options) => vcr.stop(options);
  window.saveVCR = (filename) => vcr.save(filename);
  window.loadVCR = (source) => vcr.load(source);
  window.playVCR = (options) => vcr.play(options);
  window.stopPlayback = () => vcr.stopPlayback();

  // Printed on every load so "is the recorder in this build?" is answerable at
  // a glance - a stale tab or an undeployed bundle simply will not show it.
  console.info(`[VCR] armed${app ? ` (${app})` : ''} - startVCR() / stopVCR() to record, loadVCR() / playVCR() to replay`);

  return vcr;
}

export default installPubSubVCR;
